import { useEffect, useState, useCallback } from "react";
import { Search, ShieldCheck, GraduationCap, Trash2, Loader2, Users as UsersIcon, Mail, CheckCircle2, XCircle } from "lucide-react";
import AppShell from "@/components/AppShell";
import { FullPageLoader } from "@/components/Common";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function FounderAdmin() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [deleteFor, setDeleteFor] = useState(null);

  const load = useCallback((query = "") => {
    api.get(`/founder/users${query ? `?q=${encodeURIComponent(query)}` : ""}`)
      .then(({ data }) => setData(data)).catch(() => setData({ users: [], total: 0, founders: 0, students: 0 }));
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { const t = setTimeout(() => load(q), 300); return () => clearTimeout(t); }, [q, load]);

  const toggleRole = async (u) => {
    setBusyId(u.user_id);
    try {
      await api.post(`/founder/users/${u.user_id}/role`, { make_founder: u.role !== "founder" });
      toast.success(`${u.name} is now a ${u.role === "founder" ? "student" : "founder"}.`);
      load(q);
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't change role."); }
    finally { setBusyId(null); }
  };

  const doDelete = async () => {
    const u = deleteFor;
    setBusyId(u.user_id);
    try {
      await api.delete(`/founder/users/${u.user_id}`);
      toast.success(`${u.name} and all their data were deleted.`);
      setDeleteFor(null); load(q);
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed."); }
    finally { setBusyId(null); }
  };

  if (!data) return <FullPageLoader label="Loading users…" />;

  const Stat = ({ label, value }) => (
    <div className="rounded-xl border border-brand-line bg-white px-4 py-3">
      <p className="font-heading text-2xl font-bold text-brand-deep">{value}</p>
      <p className="text-xs text-slate-500">{label}</p>
    </div>
  );

  return (
    <AppShell>
      <div className="mx-auto max-w-5xl px-8 py-10 ltifade" data-testid="founder-admin">
        <div className="flex items-center gap-2">
          <UsersIcon size={20} className="text-brand" />
          <h1 className="font-heading text-2xl font-semibold tracking-tight">Admin · Users</h1>
        </div>
        <p className="mt-1 text-slate-500">Manage everyone who's signed in. Change roles or remove a user and all their data.</p>

        <div className="mt-6 grid grid-cols-3 gap-3">
          <Stat label="Total users" value={data.total} />
          <Stat label="Founders" value={data.founders} />
          <Stat label="Students" value={data.students} />
        </div>

        <div className="relative mt-6">
          <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input data-testid="admin-user-search" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name or email"
            className="w-full rounded-xl border border-brand-line bg-white py-2.5 pl-10 pr-4 text-sm focus:border-brand focus:outline-none" />
        </div>

        <div className="mt-4 overflow-hidden rounded-xl border border-brand-line bg-white" data-testid="admin-users-table">
          {data.users.length === 0 ? (
            <p className="p-8 text-center text-sm text-slate-500">No users match that search.</p>
          ) : data.users.map((u) => (
            <div key={u.user_id} data-testid={`admin-user-${u.user_id}`} className="flex flex-wrap items-center gap-3 border-b border-brand-line px-4 py-3 last:border-0">
              <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-brand-surface text-xs font-semibold text-brand-text">
                {(u.name || "?").slice(0, 1).toUpperCase()}
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{u.name}
                  {u.user_id === user?.user_id && <span className="ml-2 text-xs text-slate-400">(you)</span>}
                </p>
                <p className="flex items-center gap-1.5 truncate text-xs text-slate-500">
                  <Mail size={11} /> {u.email}
                  <span className="text-slate-300">·</span>
                  {u.auth_provider === "email"
                    ? (u.email_verified ? <span className="inline-flex items-center gap-0.5 text-emerald-600"><CheckCircle2 size={11} /> verified</span>
                                        : <span className="inline-flex items-center gap-0.5 text-amber-600"><XCircle size={11} /> unverified</span>)
                    : <span>Google</span>}
                </p>
              </div>
              <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ${u.role === "founder" ? "bg-brand-surface text-brand-text" : "bg-slate-100 text-slate-600"}`} data-testid={`role-badge-${u.user_id}`}>
                {u.role === "founder" ? <ShieldCheck size={12} /> : <GraduationCap size={12} />} {u.role}
              </span>
              <button
                data-testid={`toggle-role-${u.user_id}`}
                onClick={() => toggleRole(u)}
                disabled={busyId === u.user_id || u.user_id === user?.user_id}
                className="rounded-lg border border-brand-line px-3 py-1.5 text-xs font-medium text-brand-text transition-all hover:bg-brand-surface disabled:opacity-40"
              >
                {u.role === "founder" ? "Make student" : "Make founder"}
              </button>
              <button
                data-testid={`delete-user-${u.user_id}`}
                onClick={() => setDeleteFor(u)}
                disabled={u.user_id === user?.user_id}
                className="rounded-lg p-1.5 text-slate-400 transition-all hover:bg-red-50 hover:text-red-600 disabled:opacity-30"
                aria-label="Delete user"
              >
                {busyId === u.user_id ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} />}
              </button>
            </div>
          ))}
        </div>
      </div>

      <AlertDialog open={!!deleteFor} onOpenChange={(o) => !o && setDeleteFor(null)}>
        <AlertDialogContent data-testid="admin-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {deleteFor?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes {deleteFor?.email} and all of their data (business, assets, drafts, progress).
              This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="admin-delete-confirm" onClick={(e) => { e.preventDefault(); doDelete(); }} className="bg-red-600 hover:bg-red-700">Delete user</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppShell>
  );
}
